import { useQuery } from '@tanstack/react-query';
import { BookOpen, Globe, MessageCircle, Send } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { openExternal, openTelegramLink } from '@/lib/telegram';
import { useI18n } from '@/i18n';
import { Notice, PageLoading, PageTitle } from '@/components/ui';
import type { Support } from '@/types/api';

export function SupportPage() {
    const { t, te } = useI18n();
    const q = useQuery({ queryKey: ['support'], queryFn: async () => (await api.get<Support>('/support')).data, staleTime: 5 * 60_000 });
    if (q.isLoading) return <PageLoading />;
    if (q.isError || !q.data) return <Notice tone="warn">{te(apiError(q.error))}</Notice>;
    const s = q.data;
    const items = [
        s.telegram && { icon: Send, label: t('support.telegram'), hint: s.telegram.handle, go: () => openTelegramLink(s.telegram!.url) },
        s.whatsapp && { icon: MessageCircle, label: t('support.whatsapp'), hint: '', go: () => openExternal(s.whatsapp as string) },
        s.faqUrl && { icon: BookOpen, label: t('support.faq'), hint: '', go: () => openTelegramLink(s.faqUrl as string) },
        s.siteUrl && { icon: Globe, label: t('support.site'), hint: s.siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, ''), go: () => openExternal(s.siteUrl) }
    ].filter(Boolean) as { icon: typeof Send; label: string; hint: string; go: () => void }[];
    return (
        <div className="space-y-3">
            <PageTitle title={t('support.title')} subtitle={t('support.subtitle')} />
            {items.map(i => (
                <button key={i.label} className="card card-tap w-full flex items-center gap-3 text-left" onClick={i.go}>
                    <div className="p-2.5 rounded-xl bg-brand-400/10 text-brand-300 border border-brand-400/20"><i.icon className="w-5 h-5" /></div>
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold">{i.label}</div>
                        {i.hint && <div className="text-[11px] text-ink-muted truncate">{i.hint}</div>}
                    </div>
                </button>
            ))}
        </div>
    );
}
